// src/view/audio.ts — WebAudio 8 kanal havuzu + olay → ses adı tablosu (DESIGN §11 "ses ipuçları", ARCHITECTURE §9). Varlık yoksa
// sessiz çalışır, hata yok (ilk adımda dosya yok). Aynı karede aynı ad ≤ 1 (beginFrame). AudioContext yoksa (Node) tamamen pasif.
import type { Fighter, SimEvent, SimWorld } from '../core/types.ts';
import { Ev, HitKind, MovePhase } from '../core/types.ts';

/** DESIGN §11 listesi + geri sayım/ayak sesi (belge listesinde yok; görsel eşleri var → eklendi) + arayüz sesleri (menü/kaplama
 *  gezintisi: DESIGN §11'de yok, çünkü belge dövüş seslerini sayıyor — menü de ses istiyor, `ui_` öneki ayrımı korur).
 *  Her ad için `public/audio/<ad>.ogg` bulunmalı (test/audio.test.ts bunu doğrular). */
export const SOUND_NAMES = [
  'whoosh_heavy', 'hit_light', 'hit_mid', 'hit_finisher', 'hit_tumble', 'block', 'guard_break', 'parry',
  'grab_grip', 'throw_yell', 'body_thud', 'bowling', 'mash_tick', 'combo_click', 'control_tick', 'land', 'edge_alarm',
  'ringout_fall', 'ringout_tok', 'respawn_chime', 'pickup', 'weapon_clang', 'weapon_drop', 'weapon_break', 'weapon_throw', 'taunt_laugh',
  'footstep', 'countdown_tick', 'match_start', 'item_spawn',
  'ui_move', 'ui_select', 'ui_back', 'ui_start',
] as const;
export type SoundName = (typeof SOUND_NAMES)[number];
const NAME_SET: ReadonlySet<string> = new Set(SOUND_NAMES);
/** Kanal sayısı (ARCHITECTURE §9: 8 kanal). */
const CHANNELS = 8;
/** dB → lineer kazanç (miksaj sayıları dB yazılsın: kulak dB düşünür, 0.22 değil). */
const dB = (d: number): number => Math.pow(10, d / 20);

/** Ses adına KALICI miksaj düzeltmesi; `play()`'in `gain` argümanıyla ÇARPILIR (çağrı yerindeki bağlamsal kısma korunur).
 *  Burada yoksa kazanç 1 (varlıkların hemen hepsi zaten ≈ −1 dBFS tepeye normalize; ölçüm: ffmpeg volumedetect).
 *
 *  NEDEN tablo (eskiden yalnız combo_click'in özel kazancı vardı): kullanıcı geri bildirimi "savurma sesi çok baskın, vurunca
 *  çıkan sesi duyamıyorum bile". Karışımın tamamı tek yerde okunabilsin diye ad → kazanç tablosuna taşındı.
 *  Ne kadar, neden:
 *   • `whoosh_heavy` −13 dB: DESIGN §4/§12 gereği ağır hamlenin tell'i kalmalı (active'ten ≥ 150 ms önce; bot tehdit algısı da
 *     buna bağlı), ama 0.55 s'lik sürekli gürültüsü (mean −17.9 dBFS) isabetlerin üstüne biniyordu. −13 dB ile mean ≈ −31 dBFS:
 *     "geliyor" ipucu duyulur, isabetlerin ~16 dB altında kalır. `whoosh_light` ise tamamen kaldırıldı (config/moves.ts).
 *   • `hit_light` +5 dB: kütüphanedeki tek düşük tepeli varlık (−6.6 dBFS; diğer isabetler ≈ −1). Jab en sık isabet ama en
 *     kısık duyuluyordu; +5 dB tepesini −1.6 dBFS'e getirir (kırpma yok) ve isabet ailesini aynı seviyeye hizalar.
 *   • `combo_click` −18 dB: DESIGN §11'den birebir (öğretici yerine geçen ince tık, dövüşün önüne geçmemeli).
 *  Dışa açık: karışım testten/ölçüm betiğinden okunabilsin (sayıyı iki yere yazmayalım). */
export const SOUND_GAIN: Readonly<Partial<Record<SoundName, number>>> = {
  whoosh_heavy: dB(-13),
  hit_light: dB(+5),
  combo_click: dB(-18),
};
const DEFAULT_EXT = 'ogg';
/** Ses hızı rampası (sn): ani değişim tık üretir, uzun rampa ağır çekimi kaçırır. 4. turda ZAMAN EĞRİSİYLE (DESIGN §10)
 *  yeniden ölçüldü ve 30 ms'te BIRAKILDI: eğri pencere boyunca 14 ayrı çarpan üretiyor, ilk basamak (1.0 → 0.12) 139 ms
 *  gerçek süren bir sim tick'i içinde geçtiği için 30 ms rampa rahat sığıyor (temiz "teyp durması"); kuyruktaki
 *  basamaklar 19–25 ms sürüyor, yani rampa hedefe varmadan yenileniyor — bu bir kusur DEĞİL, perdeyi basamaklı değil
 *  SÜREKLİ çıkarıyor (en fazla ~30 ms gecikme, duyulmuyor). Daha kısa rampa basamakları duyulur hâle getirirdi. */
const AUDIO_RATE_RAMP = 0.03;
/** İlk jestte istenen ses, yükleme bitene kadar kuyrukta bekler; bu süreden geç gelirse çalınmaz (alakasız anda patlamasın). */
const LATE_PLAY_SEC = 1.5;
/** Ses dosyalarının kök yolu: `public/audio/` → Vite bunu kökten servis eder (dev ve build aynı). */
const DEFAULT_BASE_URL = '/audio';
/** Arayüz gezinti sesi: kind → ad + kazanç. `move` sık çalar (her odak değişimi) → daha kısık. */
const UI_SOUNDS: Readonly<Record<UiSoundKind, readonly [SoundName, number]>> = {
  move: ['ui_move', 0.5], select: ['ui_select', 0.8], back: ['ui_back', 0.8], start: ['ui_start', 0.9],
};
/** Arayüz sesi türü (uiNav.onNavSound + menü "başlat"). */
export type UiSoundKind = 'move' | 'select' | 'back' | 'start';

/** Olay → ses adı (saf; test edilir). null = ses yok. */
export function soundForEvent(e: SimEvent, world: SimWorld): SoundName | null {
  switch (e.type) {
    case Ev.MoveStart: {
      const mv = world.moves[e.move];
      const s = mv?.sfxWindup;
      return s && NAME_SET.has(s) ? (s as SoundName) : null;
    }
    case Ev.PhaseChange: {
      // Recover'a giriş + iptal penceresi var → combo_click (öğretici yerine)
      const f = world.fighters[e.actor]; const mv = world.moves[e.move >= 0 ? e.move : (f?.moveIdx ?? -1)];
      return f && mv && f.phase === MovePhase.Recover && mv.cancels.length > 0 ? 'combo_click' : null;
    }
    case Ev.Hit: {
      switch (e.v2 as HitKind) {
        case HitKind.Finisher: return 'hit_finisher';
        case HitKind.Tumble: return 'hit_tumble';
        case HitKind.Mid: return 'hit_mid';
        default: return 'hit_light';
      }
    }
    case Ev.Blocked: return 'block';
    case Ev.Parry: return 'parry';
    case Ev.GuardBreak: return 'guard_break';
    case Ev.Grab: case Ev.Hold: return 'grab_grip';
    // Ev.Throw'un SESİ YOK: efor bağırışı hamlenin başında (G2 `sfxWindup`) çalıyor. İkisi birden açıkken aynı ses
    // 19 tick arayla iki kez çalıyordu ("iuvv iuvv" — kullanıcı bulgusu); kare içi tekrar koruması araya 19 tick
    // girdiği için yakalayamıyor. Bırakma anının kendi vurgusu var: darbe donması + bedenin uçması + iniş sesi.
    case Ev.Throw: return null;
    case Ev.Knockdown: return 'body_thud';
    case Ev.BodyImpact: return 'bowling';
    case Ev.TumbleControl: return 'control_tick';
    case Ev.Land: return 'land';
    case Ev.EdgeAlarm: return 'edge_alarm';
    case Ev.Fell: return 'ringout_fall';
    case Ev.Respawn: return 'respawn_chime';
    case Ev.ItemSpawn: return 'item_spawn';
    case Ev.ItemPickup: return 'pickup';
    case Ev.ItemHit: return 'weapon_clang';
    case Ev.ItemDrop: return 'weapon_drop';
    case Ev.ItemThrow: return 'weapon_throw';   // silahı fırlatma anı (eskiden sessizdi)
    case Ev.ItemBreak: return 'weapon_break';
    case Ev.Taunt: return 'taunt_laugh';
    case Ev.Countdown: return 'countdown_tick';
    case Ev.MatchStart: return 'match_start';
    default: return null;
  }
}

interface Channel { src: AudioBufferSourceNode; startedAt: number; rate: number }

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<SoundName, AudioBuffer | null>();
  private readonly channels: Channel[] = [];
  private readonly frameNames = new Set<string>();
  private loading: Promise<void> | null = null;
  volume = 1;
  /** Sessize alma (kullanıcı: "testlerini sessiz yap, rahatsız ediyor beni; ses kapama açma olsun"). `volume`
   *  KORUNUR — geri açınca eski seviye döner. Ses grafiği bozulmaz: yalnız master kazancı 0'a iner, böylece
   *  `played`/`missing` sayaçları ve eğitim geri bildirimleri sessizken de aynı yolu izler. Kalıcılık ve düğme
   *  view/settings.ts'te; Audio yalnız durumu tutar. */
  muted = false;
  /** Oyun zaman çarpanı (1 = normal; vuruş ağır çekiminde eğri boyunca 0.12 → 0.88). `setTimeScale` yazar; `play` yeni
   *  sesleri buna göre başlatır. */
  private timeScale = 1;
  /** İstatistik: çalınan / eksik varlık nedeniyle atlanan. */
  played = 0; missing = 0;
  /** Yalnız bu adlar fetch edilir → eksik varlık için 404 isteği yok. null (varsayılan) = tüm SOUND_NAMES denenir; artık her ad için
   *  dosya var (test/audio.test.ts), o yüzden oyun bunu null bırakır. Boş küme = hiç ses yükleme (duman testi: ölçüm saflığı). */
  available: ReadonlySet<string> | null = null;

  constructor(readonly baseUrl = DEFAULT_BASE_URL, readonly ext = DEFAULT_EXT) {}

  /** Tarayıcı jestinde (menü tıklaması) çağrılır; AudioContext yoksa sessiz kalır. */
  unlock(): void {
    if (this.ctx) { if (this.ctx.state === 'suspended') void this.ctx.resume(); return; }
    const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    this.master.connect(this.ctx.destination);
    void this.load();
  }
  get enabled(): boolean { return this.ctx !== null; }

  /** Tüm adları dener; 404/decode hatası → null (sessiz). Bir kez. */
  load(): Promise<void> {
    if (this.loading) return this.loading;
    const ctx = this.ctx;
    if (!ctx || typeof fetch === 'undefined') return Promise.resolve();
    const names = this.available ? SOUND_NAMES.filter((n) => this.available!.has(n)) : SOUND_NAMES;
    this.loading = Promise.all(names.map(async (name) => {
      try {
        const res = await fetch(`${this.baseUrl}/${name}.${this.ext}`);
        if (!res.ok) { this.buffers.set(name, null); return; }
        const buf = await ctx.decodeAudioData(await res.arrayBuffer());
        this.buffers.set(name, buf);
      } catch { this.buffers.set(name, null); }
    })).then(() => undefined);
    return this.loading;
  }

  setVolume(v: number): void { this.volume = v; if (this.master) this.master.gain.value = this.muted ? 0 : v; }
  /** Sesi kapat/aç. Kilit açılmadan (AudioContext yokken) da çağrılabilir: durum saklanır, `unlock()` uygular. */
  setMuted(on: boolean): void { this.muted = on; if (this.master) this.master.gain.value = on ? 0 : this.volume; }

  /**
   * Oyun zamanı yavaşlayınca SES DE yavaşlar (perde düşer) — ağır çekimin okunmasını sağlayan asıl şey bu.
   * Kullanıcı "darbe sesini de ağır çekim yapmak lazım ki anlaşılsın" dedi: görüntü yavaşlayıp ses normal hızda
   * devam edince beyin yavaşlamayı yakalamıyor.
   *
   * İki tarafa birden uygulanır: (a) O AN ÇALAN kanalların `playbackRate`i kısa bir rampayla düşürülür — darbe sesi
   * ağır çekimle aynı anda başladığı için asıl duyulan bu; (b) ağır çekim sürerken BAŞLAYAN sesler de yavaş başlar.
   * Rampa 30 ms: ani sıçrama "tık" sesi üretir, uzun rampa ise ağır çekimi kaçırır.
   */
  setTimeScale(scale: number): void {
    const s = scale > 0 ? scale : 1;
    if (Math.abs(s - this.timeScale) < 1e-3) return;
    this.timeScale = s;
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime, ramp = t + AUDIO_RATE_RAMP;
    for (let i = 0; i < this.channels.length; i++) {
      const ch = this.channels[i]!;
      try {
        ch.src.playbackRate.cancelScheduledValues(t);
        ch.src.playbackRate.setValueAtTime(ch.src.playbackRate.value, t);
        ch.src.playbackRate.linearRampToValueAtTime(ch.rate * s, ramp);
      } catch { /* kaynak bitmiş olabilir */ }
    }
  }
  /** Kare başı: aynı ad tekrarını sıfırlar (4-tick karede aynı ses ≤ 1). */
  beginFrame(): void { this.frameNames.clear(); }

  play(name: SoundName, gain = 1, rate = 1): boolean {
    if (this.frameNames.has(name)) return false;
    this.frameNames.add(name);
    gain *= SOUND_GAIN[name] ?? 1;   // kalıcı miksaj düzeltmesi × çağrı yerindeki bağlamsal kısma
    const ctx = this.ctx, master = this.master;
    const buf = this.buffers.get(name);
    // Tampon HENÜZ yüklenmediyse (ilk jest: `unlock()` yüklemeyi başlatır ama beklemez) sesi ATLAMA, KUYRUĞA AL:
    // menüdeki ilk tıklamanın onay sesi bu yüzden hiç duyulmuyordu. Yükleme bitince çalınır; ancak GEÇ kalırsa
    // (ör. ağ yavaş) anlamsız biçimde alakasız bir anda patlamasın diye kısa bir son kullanma süresi var.
    if (ctx && master && !buf && this.loading && !this.buffers.has(name)) {
      const deadline = ctx.currentTime + LATE_PLAY_SEC;
      void this.loading.then(() => {
        if (this.ctx && this.ctx.currentTime <= deadline && this.buffers.get(name)) {
          this.frameNames.delete(name);   // kuyruktan çalınan ses kare kilidine takılmasın
          this.play(name, gain, rate);
        }
      });
      return false;
    }
    if (!ctx || !master || !buf) { this.missing++; return false; }
    if (this.channels.length >= CHANNELS) {
      let oldest = 0;
      for (let i = 1; i < this.channels.length; i++) if (this.channels[i]!.startedAt < this.channels[oldest]!.startedAt) oldest = i;
      try { this.channels[oldest]!.src.stop(); } catch { /* zaten bitti */ }
      this.channels.splice(oldest, 1);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf; src.playbackRate.value = rate * this.timeScale;   // ağır çekimde yeni sesler de yavaş başlar
    const g = ctx.createGain(); g.gain.value = gain;
    src.connect(g); g.connect(master);
    const ch: Channel = { src, startedAt: ctx.currentTime, rate };   // `rate` = sesin KENDİ hızı; zaman çarpanı ayrı çarpılır
    src.onended = () => { const i = this.channels.indexOf(ch); if (i >= 0) this.channels.splice(i, 1); };
    src.start();
    this.channels.push(ch);
    this.played++;
    return true;
  }

  onEvent(e: SimEvent, world: SimWorld): void {
    const name = soundForEvent(e, world);
    if (!name) return;
    if (name === 'ringout_fall') { this.play(name); this.play('ringout_tok', 0.5); return; }
    this.play(name);
  }
  /** Arayüz sesi (menü, duraklatma/kazanan kaplaması; uiNav.onNavSound kancasının karşılığı). Ses bağımlılığı input katmanına
   *  girmesin diye kanca `kind` dizgesi taşır, `SoundName` değil (game → view → anim → sim tek yön). */
  ui(kind: UiSoundKind): void { const [name, gain] = UI_SOUNDS[kind]; this.play(name, gain); }
  /** Ayak sesi (fighterView view-yerel callback'i); hafif perde çeşitlemesi (view'da Math.random serbest). */
  footstep(_f: Fighter, _side: 0 | 1): void { this.play('footstep', 0.5, 0.9 + Math.random() * 0.2); }
  /** Kurtulma mash basışı (HUD/oyuncu girdisi). */
  mashTick(): void { this.play('mash_tick', 0.7); }

  dispose(): void {
    for (const c of this.channels) { try { c.src.stop(); } catch { /* */ } }
    this.channels.length = 0;
    void this.ctx?.close();
    this.ctx = null; this.master = null;
  }
}
